'use client';

import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@core/ui/primitives/dialog';
import { Button } from '@core/ui/primitives/button';
import { Tabs, TabsList, TabsTrigger } from '@core/ui/primitives/tabs';
import { Input } from '@core/ui/primitives/input';
import { Textarea } from '@core/ui/primitives/textarea';
import { cn } from '@core/helpers/misc/GeneralUtils';

type FeedbackType = 'bug' | 'enhancement' | 'question';

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [subject, setSubject] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [type, setType] = React.useState<FeedbackType>('question');
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState<{ success?: boolean; error?: string } | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSubject('');
      setDescription('');
      setType('question');
      setSubmitting(false);
      setResult(null);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, description, type }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'Submission failed');
      setResult({ success: true });
    } catch (err: any) {
      setResult({ error: err?.message || 'Submission failed' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-2xl')}>
        {!result?.success && (
          <DialogHeader>
            <DialogTitle>Send Feedback</DialogTitle>
            <DialogDescription>Share a bug, question, or enhancement idea.</DialogDescription>
          </DialogHeader>
        )}

        {result?.success ? (
          <div className='flex flex-col items-center justify-center gap-4 py-8'>
            <div className='flex h-14 w-14 items-center justify-center rounded-full bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'>
              <svg className='h-7 w-7 text-green-600 dark:text-green-400' aria-hidden='true'>
                <use href='/app-icons.svg#fas-check' />
              </svg>
            </div>
            <div className='text-center space-y-1'>
              <h3 className='text-lg font-semibold'>Feedback submitted</h3>
              <p className='text-sm text-muted-foreground'>Thank you — we’ll review it and follow up internally.</p>
              <p className='text-xs text-muted-foreground'>
                No public links are shown because this repository is private.
              </p>
            </div>
            <div className='mt-2 flex items-center gap-2'>
              <Button
                onClick={() => {
                  setSubject('');
                  setDescription('');
                  setType('question');
                  setResult(null);
                }}>
                Send another
              </Button>
              <Button variant='ghost' onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className='mt-2 flex flex-col gap-3'>
            <div>
              <label className='block text-sm font-medium mb-1'>Type</label>
              <Tabs value={type} onValueChange={(v) => setType(v as FeedbackType)} className='w-fit rounded-lg'>
                <TabsList className='gap-1'>
                  <TabsTrigger value='bug' className='text-xs sm:text-sm'>
                    Bug
                  </TabsTrigger>
                  <TabsTrigger value='enhancement' className='text-xs sm:text-sm'>
                    Enhancement
                  </TabsTrigger>
                  <TabsTrigger value='question' className='text-xs sm:text-sm'>
                    Question
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Subject</label>
              <Input
                type='text'
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder='Short summary'
                maxLength={200}
                required
              />
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Description</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder='Describe the issue or request'
                rows={6}
                className='min-h-[7.5rem] resize-y'
                required
              />
            </div>

            {result?.error && <div className='text-sm text-red-600'>{result.error}</div>}

            <div className='mt-2 flex justify-end gap-2'>
              <Button type='button' variant='ghost' onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type='submit' disabled={submitting || !subject || !description}>
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackDialog;
